import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner
} from 'react-bootstrap';
import { FaSave, FaTimes } from 'react-icons/fa';

const EditMedicine = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'Tablet',
    dosageInstruction: {
      morning: false,
      afternoon: false,
      evening: false,
      night: false,
      beforeFood: false,
      afterFood: false
    },
    stock: 0,
    unit: 'Tabs',
    manufacturer: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const { name, type, dosageInstruction, stock, unit, manufacturer } = formData;
  
  useEffect(() => {
    // Check permission
    if (user && user.role !== 'admin' && !user.permissions?.editMedicine) {
      navigate('/medicines');
      return;
    }
    
    const fetchMedicine = async () => {
      try {
        const res = await axios.get(`/api/medicines.php?id=${id}`);
        const medicineData = res.data;
        
        setFormData({
          name: medicineData.name || '',
          type: medicineData.type || 'Tablet',
          dosageInstruction: medicineData.dosageInstruction || {
            morning: false,
            afternoon: false,
            evening: false,
            night: false,
            beforeFood: false,
            afterFood: false
          },
          stock: medicineData.stock || 0,
          unit: medicineData.unit || 'Tabs',
          manufacturer: medicineData.manufacturer || ''
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching medicine:', err);
        setError('Failed to load medicine data');
        setLoading(false);
      }
    };
    
    fetchMedicine();
  }, [id, user, navigate]);
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const onChangeNumber = e => {
    setFormData({ ...formData, [e.target.name]: Number(e.target.value) });
  };
  
  const onChangeDosage = e => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      dosageInstruction: {
        ...dosageInstruction,
        [name]: checked
      }
    });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      // Validate form
      if (!name || !type) {
        setError('Please fill all required fields');
        setSaving(false);
        return;
      }
      
      await axios.put(`/api/medicines.php?id=${id}`, formData);
      
      setSaving(false);
      navigate('/medicines');
    } catch (err) {
      setError(err.response?.data.msg || 'Something went wrong');
      setSaving(false);
    }
  };
  
  const medicineTypes = ['Tablet', 'Capsule', 'Syrup', 'Injection', 'Drip', 'Ointment', 'Other'];
  const unitOptions = ['Tabs', 'Caps', 'ml', 'mg', 'g', 'Bottle', 'Box', 'Pcs'];
  
  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading medicine data...</p>
      </div>
    );
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Edit Medicine</h2>
        <Link to="/medicines">
          <Button variant="outline-secondary">
            <FaTimes className="me-2" /> Cancel
          </Button>
        </Link>
      </div>
      
      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={onSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Medicine Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={name}
                    onChange={onChange}
                    placeholder="Enter medicine name"
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type *</Form.Label>
                  <Form.Select
                    name="type"
                    value={type}
                    onChange={onChange}
                    required
                  >
                    {medicineTypes.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Manufacturer</Form.Label>
                  <Form.Control
                    type="text"
                    name="manufacturer"
                    value={manufacturer}
                    onChange={onChange}
                    placeholder="Enter manufacturer"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <h5 className="mt-4 mb-3">Default Dosage Instructions</h5>
            
            <Row className="mb-3">
              <Col xs={6} md={3}>
                <Form.Check
                  type="checkbox"
                  id="morning"
                  name="morning"
                  label="Morning"
                  checked={dosageInstruction.morning}
                  onChange={onChangeDosage}
                />
              </Col>
              <Col xs={6} md={3}>
                <Form.Check
                  type="checkbox"
                  id="afternoon"
                  name="afternoon"
                  label="Afternoon"
                  checked={dosageInstruction.afternoon}
                  onChange={onChangeDosage}
                />
              </Col>
              <Col xs={6} md={3}>
                <Form.Check
                  type="checkbox"
                  id="evening"
                  name="evening"
                  label="Evening"
                  checked={dosageInstruction.evening}
                  onChange={onChangeDosage}
                />
              </Col>
              <Col xs={6} md={3}>
                <Form.Check
                  type="checkbox"
                  id="night"
                  name="night"
                  label="Night"
                  checked={dosageInstruction.night}
                  onChange={onChangeDosage}
                />
              </Col>
            </Row>
            
            <Row className="mb-4">
              <Col xs={6}>
                <Form.Check
                  type="checkbox"
                  id="beforeFood"
                  name="beforeFood"
                  label="Before Food"
                  checked={dosageInstruction.beforeFood}
                  onChange={onChangeDosage}
                />
              </Col>
              <Col xs={6}>
                <Form.Check
                  type="checkbox"
                  id="afterFood"
                  name="afterFood"
                  label="After Food"
                  checked={dosageInstruction.afterFood}
                  onChange={onChangeDosage}
                />
              </Col>
            </Row>
            
            <div className="d-grid">
              <Button
                variant="primary"
                type="submit"
                disabled={saving}
              >
                {saving ? (
                  'Saving...'
                ) : (
                  <>
                    <FaSave className="me-2" /> Update Medicine
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default EditMedicine; 
